import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Wine, Globe } from '@phosphor-icons/react';
import { Breadcrumb } from '../Product/Breadcrumb';

// Import country data functions
import { getFranceData } from '../../data/countries/france';
import { getSpainData } from '../../data/countries/spain';
import { getPortugalData } from '../../data/countries/portugal';
import { getItalyData } from '../../data/countries/italy';
import { getArgentinaData } from '../../data/countries/argentina';
import { getChileData } from '../../data/countries/chile';
import { getAustraliaData } from '../../data/countries/australia';
import { getAustriaData } from '../../data/countries/austria';
import { getSwitzerlandData } from '../../data/countries/switzerland';
import { getMexicoData } from '../../data/countries/mexico';
import { getGermanyData } from '../../data/countries/germany';

// Data mapping object - using functions
const countryDataMap = {
  'france': getFranceData,
  'spain': getSpainData,
  'portugal': getPortugalData,
  'italy': getItalyData,
  'argentina': getArgentinaData,
  'chile': getChileData,
  'australia': getAustraliaData,
  'austria': getAustriaData,
  'switzerland': getSwitzerlandData,
  'mexico': getMexicoData,
  'germany': getGermanyData,
};

const RegionsListPage = () => {
  const { country } = useParams();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selectedFilter, setSelectedFilter] = React.useState('all');

  React.useEffect(() => {
    setLoading(true);
    setError(null);
    
    const key = country;
    const getCountryData = countryDataMap[key];
    
    if (getCountryData) {
      setData(getCountryData());
      setLoading(false);
    } else {
      navigate('/404', { replace: true });
    }
  }, [country, navigate]);

  // Get unique first letters from region names
  const getRegionLetters = () => {
    if (!data?.regions) return [];
    const letters = [...new Set(data.regions.map(region => region.name.charAt(0).toUpperCase()))];
    return letters.sort();
  };

  // Filter regions based on selected letter
  const getFilteredRegions = () => {
    if (!data?.regions) return [];
    if (selectedFilter === 'all') return data.regions;
    return data.regions.filter(region => 
      region.name.charAt(0).toUpperCase() === selectedFilter
    );
  };

  if (loading) {
    return <main style={{ padding: 40, textAlign: 'center' }}>Chargement...</main>;
  }
  if (error) {
    return (
      <main style={{ padding: 40, textAlign: 'center' }}>
        <h1>Information non disponible</h1>
        <p>{error}</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: '#4B164C',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >Retour</button>
      </main>
    );
  }
  if (!data) return null;

  const regionLetters = getRegionLetters();
  const filteredRegions = getFilteredRegions();

  return (
    <main style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Breadcrumb />
      
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px', paddingBottom: '16px', borderBottom: '1px solid #e8e0e8' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
          onMouseOver={e => e.currentTarget.style.background = '#f0e6f0'}
          onMouseOut={e => e.currentTarget.style.background = 'none'}
        >
          <ArrowLeft size={24} color="#4B164C" />
        </button>
        <div>
          <h1 style={{ fontSize: '32px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '700' }}>
            Régions de {data.name}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '16px' }}>
            <MapPin size={16} />
            <span>{data.regions?.length || 0} régions viticoles</span>
          </div>
        </div>
      </div>

      {/* Letter Filter */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '8px', 
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <span style={{ 
            fontSize: '16px', 
            color: '#4B164C', 
            fontWeight: '600',
            marginRight: '8px'
          }}>
            Filtrer par lettre :
          </span>
          
          {/* All button */}
          <button
            onClick={() => setSelectedFilter('all')}
            style={{
              padding: '8px 16px',
              background: selectedFilter === 'all' ? '#4B164C' : '#f0e6f0',
              color: selectedFilter === 'all' ? 'white' : '#4B164C',
              border: '2px solid #4B164C',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseOver={e => {
              if (selectedFilter !== 'all') {
                e.currentTarget.style.background = '#4B164C';
                e.currentTarget.style.color = 'white';
              }
            }}
            onMouseOut={e => {
              if (selectedFilter !== 'all') {
                e.currentTarget.style.background = '#f0e6f0';
                e.currentTarget.style.color = '#4B164C';
              }
            }}
          >
            Toutes ({data.regions?.length || 0})
          </button>
          
          {/* Letter buttons */}
          {regionLetters.map(letter => (
            <button
              key={letter}
              onClick={() => setSelectedFilter(letter)}
              style={{
                padding: '8px 16px',
                background: selectedFilter === letter ? '#4B164C' : '#f0e6f0',
                color: selectedFilter === letter ? 'white' : '#4B164C',
                border: '2px solid #4B164C',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseOver={e => {
                if (selectedFilter !== letter) {
                  e.currentTarget.style.background = '#4B164C';
                  e.currentTarget.style.color = 'white';
                }
              }}
              onMouseOut={e => {
                if (selectedFilter !== letter) {
                  e.currentTarget.style.background = '#f0e6f0';
                  e.currentTarget.style.color = '#4B164C';
                }
              }}
            >
              {letter} ({data.regions?.filter(region => region.name.charAt(0).toUpperCase() === letter).length || 0})
            </button>
          ))}
        </div>
        
        {/* Results count */}
        <div style={{ 
          fontSize: '14px', 
          color: '#666',
          fontStyle: 'italic'
        }}>
          {selectedFilter === 'all' 
            ? `Affichage de toutes les ${data.regions?.length || 0} régions`
            : `Affichage de ${filteredRegions.length} région${filteredRegions.length > 1 ? 's' : ''} commençant par "${selectedFilter}"`
          }
        </div>
      </div>

      {/* Regions Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '40px' }}>
        {filteredRegions.map((region, idx) => (
          <Link
            key={idx}
            to={`/info/countries/${country}/regions/${region.slug || region.name.toLowerCase().replace(/\s+/g, '-')}`}
            style={{
              display: 'block',
              background: '#fff',
              borderRadius: '16px',
              border: '2px solid #e8e0e8',
              padding: '24px',
              textDecoration: 'none',
              color: 'inherit',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
            onMouseOver={e => {
              e.currentTarget.style.borderColor = '#4B164C';
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.borderColor = '#e8e0e8';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
              <div style={{ 
                width: '60px', 
                height: '60px', 
                background: data.mapAreas && data.mapAreas[idx]?.color || '#4B164C', 
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '20px',
                fontWeight: 'bold'
              }}>
                {region.name.charAt(0)}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '600' }}>
                  {region.name}
                </h3>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  {region.percentage} • {region.area}
                </div>
              </div>
            </div>
            
            <p style={{ fontSize: '16px', color: '#333', margin: '0 0 16px 0', lineHeight: '1.5' }}>
              {region.description}
            </p>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4B164C', fontSize: '14px', fontWeight: '500' }}>
              <MapPin size={16} />
              Explorer cette région
            </div>
          </Link>
        ))}
      </div>

      {/* No results message */}
      {filteredRegions.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          background: '#f8f6fa', 
          borderRadius: '16px',
          marginBottom: '40px'
        }}>
          <h3 style={{ fontSize: '20px', color: '#4B164C', marginBottom: '8px' }}>
            Aucune région trouvée
          </h3>
          <p style={{ color: '#666', fontSize: '16px' }}>
            Aucune région ne commence par la lettre "{selectedFilter}"
          </p>
          <button
            onClick={() => setSelectedFilter('all')}
            style={{
              marginTop: '16px',
              padding: '12px 24px',
              background: '#4B164C',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            Voir toutes les régions
          </button>
        </div>
      )}

      {/* Continue Exploring Navigation */}
      <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
        <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600', textAlign: 'center' }}>
          Continuez à Explorer
        </h2>
        
        <div style={{ display: 'grid', gap: '24px' }}>
          {/* Other Countries Section */}
          <div>
            <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Globe size={20} />
              Autres Pays Viticoles
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {Object.keys(countryDataMap).filter(c => c !== country).slice(0, 6).map((otherCountry, idx) => {
                const getOtherCountryData = countryDataMap[otherCountry];
                if (!getOtherCountryData) return null;
                
                const otherCountryData = getOtherCountryData();
                return (
                  <button
                    key={idx}
                    onClick={() => navigate(`/info/countries/${otherCountry}`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      background: '#fff',
                      border: '2px solid #e8e0e8',
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'left',
                      width: '100%'
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.borderColor = '#4B164C';
                      e.currentTarget.style.background = '#f0e6f0';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.borderColor = '#e8e0e8';
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      background: '#4B164C', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      {otherCountryData.name.charAt(0)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#4B164C', marginBottom: '4px' }}>
                        {otherCountryData.name}
                      </div>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        {otherCountryData.regions?.length || 0} régions
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '12px', 
            justifyContent: 'center',
            marginTop: '16px'
          }}>
            <button
              onClick={() => navigate(`/info/countries/${country}`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: '#4B164C',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
              onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
            >
              <Globe size={18} />
              Retour à {data.name}
            </button>
            <button
              onClick={() => navigate('/info/countries')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: '#6d2c7a',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#8a4a8a'}
              onMouseOut={e => e.currentTarget.style.background = '#6d2c7a'}
            >
              <Wine size={18} />
              Tous les Pays
            </button>
            <button
              onClick={() => navigate('/info')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: '#f0e6f0',
                color: '#4B164C',
                border: '2px solid #4B164C',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = '#4B164C';
                e.currentTarget.style.color = 'white';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = '#f0e6f0';
                e.currentTarget.style.color = '#4B164C';
              }}
            >
              <Wine size={18} />
              Guide du Vin
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default RegionsListPage; 